/**
 * Shared view_item helpers for the single-product page, used by both the classic jQuery generator
 * (view-item-event-generator.lazy.register.ts) and the block/Interactivity generator
 * (view-item-variation-event-generator.lazy.register.mts).
 *
 * The base event (currency + the one product item) is server-rendered as a JSON script tag. A follow-up
 * view_item for a selected variation keeps the root product's item_id and overlays the variation's own SKU
 * as item_variant plus its price.
 */

export type GaItem = Record<string, any>;

export interface ViewItemEvent {
	currency?: string;
	value?: number;
	items: GaItem[];
}

export const ITEM_SELECTOR = ".plaudit-woocommerce-ga-integration__view_item__item";

/**
 * Rounds JS's representation of the price to two decimal places
 */
export function round(value: number): number {
	return Math.round(value * 100) / 100;
}

/**
 * Reads server-rendered view_item payload on the page, keyed by the product id on each tag's
 * data-product-id attribute. This supports pages that render more than one product (e.g. several Single
 * Product blocks); on an ordinary single-product page the map simply holds one entry.
 */
const foundEvents = new Map<string, ViewItemEvent>();
export function readBaseEventData(element: Element, requireUnique = false): {productId: string, event: ViewItemEvent}|undefined {
	const productId = element.getAttribute("data-product-id");
	if (!productId || !element.textContent) {
		return;
	}
	let event = foundEvents.get(productId);
	if (event !== undefined) {
		return requireUnique ? undefined : {productId, event};
	}
	try {
		const data = JSON.parse(element.textContent);
		if (typeof data === "object" && data && Array.isArray(data.items) && data.items.length) {
			foundEvents.set(productId, data as ViewItemEvent);
			return {productId, event: data as ViewItemEvent};
		}
	} catch {
		// Skip a malformed tag rather than losing the others.
	}
}

export function findEventDataForProductId(productId: string) {
	let event = foundEvents.get(productId);
	if (event !== undefined) {
		return event;
	}
	const element = document.querySelector(`${ITEM_SELECTOR}[data-product-id="${productId}"]`);
	return element ? readBaseEventData(element)?.event : undefined;
}

export function fireViewItem(event: ViewItemEvent, eventName: 'view_item'|'select_item' = "view_item"): void {
	if (window.gtag) {
		window.gtag("event", eventName, event);
	}
}

/**
 * Builds an event payload for a selected variation by overlaying its price and variant identity onto the base
 * item. item_id stays the root product's SKU; `variant` (the variation's own SKU, or its id as a fallback)
 * becomes item_variant. Used for the select_item variation follow-up on both the classic and block paths.
 */
export function buildVariationEvent(base: ViewItemEvent, variant: string, price: number | undefined): ViewItemEvent {
	const item: GaItem = {...(base.items[0] ?? {})};

	if (variant) {
		item.item_variant = variant;
	}
	if (typeof price === 'number' && Number.isFinite(price)) {
		item.price = price;
	}

	const event: ViewItemEvent = {items: [item]};
	if (base.currency) {
		event.currency = base.currency;
	}
	if (typeof item.price === 'number') {
		event.value = round(item.price);
	}
	return event;
}
